import type { Locale, TranslationKey } from "./i18n";

type ValidationTranslationKey = Extract<TranslationKey,
  | "runLockedHoldout"
  | "runningLockedHoldout"
  | "lockedHoldoutHint"
  | "validationApplyBlocked"
  | "validationPending"
  | "validationPassed"
  | "validationRejected"
  | "lockedHoldoutFailed"
>;

type ValidationStrings = Record<ValidationTranslationKey, string>;

const completeValidationTranslations = {
  hi: {
    runLockedHoldout: "अंतिम होल्डआउट जाँच चलाएँ",
    runningLockedHoldout: "अनदेखे डेटा की जाँच हो रही है...",
    lockedHoldoutHint: "सुधारे गए उम्मीदवार को केवल एक बार की अनदेखे-डेटा जाँच पास करने के बाद ही लागू किया जा सकता है।",
    validationApplyBlocked: "सत्यापन तक लागू करना बंद है",
    validationPending: "उम्मीदवार अंतिम होल्डआउट जाँच की प्रतीक्षा कर रहा है।",
    validationPassed: "उम्मीदवार सत्यापित है। अब इसे लागू करके नया बैकटेस्ट चला सकते हैं।",
    validationRejected: "उम्मीदवार सत्यापित नहीं हुआ। मूल रणनीति बनाए रखें।",
    lockedHoldoutFailed: "अंतिम होल्डआउट जाँच पूरी नहीं हो सकी। फिर प्रयास करें।",
  },
  ur: {
    runLockedHoldout: "آخری ہولڈ آؤٹ جانچ چلائیں",
    runningLockedHoldout: "غیر استعمال شدہ ڈیٹا جانچا جا رہا ہے...",
    lockedHoldoutHint: "بہتر امیدوار کو صرف ایک بار کی غیر استعمال شدہ ڈیٹا جانچ پاس کرنے کے بعد لاگو کیا جا سکتا ہے۔",
    validationApplyBlocked: "تصدیق تک لاگو کرنا بند ہے",
    validationPending: "امیدوار آخری ہولڈ آؤٹ جانچ کا منتظر ہے۔",
    validationPassed: "امیدوار کی تصدیق ہوگئی۔ اب اسے لاگو کرکے نیا بیک ٹیسٹ چلائیں۔",
    validationRejected: "امیدوار کی تصدیق نہیں ہوئی۔ اصل حکمت عملی برقرار رکھیں۔",
    lockedHoldoutFailed: "آخری ہولڈ آؤٹ جانچ مکمل نہیں ہو سکی۔ دوبارہ کوشش کریں۔",
  },
  th: {
    runLockedHoldout: "ตรวจ holdout ขั้นสุดท้าย",
    runningLockedHoldout: "กำลังตรวจข้อมูลที่ไม่เคยใช้...",
    lockedHoldoutHint: "จะใช้ตัวเลือกที่ปรับปรุงแล้วได้ต่อเมื่อผ่านการตรวจข้อมูลที่ไม่เคยใช้แบบครั้งเดียว",
    validationApplyBlocked: "ยังใช้ไม่ได้จนกว่าจะผ่านการตรวจสอบ",
    validationPending: "ตัวเลือกกำลังรอการตรวจ holdout ขั้นสุดท้าย",
    validationPassed: "ตัวเลือกผ่านการตรวจสอบแล้ว คุณสามารถนำไปใช้และรัน backtest ใหม่ได้",
    validationRejected: "ตัวเลือกไม่ผ่านการตรวจสอบ โปรดใช้กลยุทธ์เดิมต่อไป",
    lockedHoldoutFailed: "ตรวจ holdout ขั้นสุดท้ายไม่สำเร็จ โปรดลองอีกครั้ง",
  },
  bn: {
    runLockedHoldout: "চূড়ান্ত holdout পরীক্ষা চালান",
    runningLockedHoldout: "আগে ব্যবহার না করা ডেটা পরীক্ষা হচ্ছে...",
    lockedHoldoutHint: "উন্নত প্রার্থীটি একবারের unseen-data পরীক্ষা পাস করলেই কেবল প্রয়োগ করা যাবে।",
    validationApplyBlocked: "যাচাই না হওয়া পর্যন্ত প্রয়োগ বন্ধ",
    validationPending: "প্রার্থীটি চূড়ান্ত holdout পরীক্ষার অপেক্ষায় আছে।",
    validationPassed: "প্রার্থীটি যাচাই হয়েছে। এখন এটি প্রয়োগ করে নতুন backtest চালাতে পারেন।",
    validationRejected: "প্রার্থীটি যাচাইয়ে পাস করেনি। মূল কৌশলটি রাখুন।",
    lockedHoldoutFailed: "চূড়ান্ত holdout পরীক্ষা সম্পন্ন হয়নি। আবার চেষ্টা করুন।",
  },
  id: {
    runLockedHoldout: "Jalankan pemeriksaan holdout final",
    runningLockedHoldout: "Memeriksa data yang belum pernah digunakan...",
    lockedHoldoutHint: "Kandidat hasil improve hanya dapat diterapkan setelah lolos pemeriksaan satu kali pada data yang belum pernah digunakan.",
    validationApplyBlocked: "Penerapan dikunci hingga tervalidasi",
    validationPending: "Kandidat menunggu pemeriksaan holdout final.",
    validationPassed: "Kandidat tervalidasi. Anda dapat menerapkannya dan menjalankan backtest baru.",
    validationRejected: "Kandidat tidak lolos validasi. Pertahankan strategi awal.",
    lockedHoldoutFailed: "Pemeriksaan holdout final tidak dapat diselesaikan. Coba lagi.",
  },
  fil: {
    runLockedHoldout: "Patakbuhin ang huling holdout check",
    runningLockedHoldout: "Sinusuri ang hindi pa nagamit na data...",
    lockedHoldoutHint: "Maa-apply lang ang improved candidate kapag nakapasa ito sa isang beses na unseen-data check.",
    validationApplyBlocked: "Naka-lock ang pag-apply hanggang ma-validate",
    validationPending: "Naghihintay ang candidate sa huling holdout check.",
    validationPassed: "Validated ang candidate. Maaari mo na itong i-apply at magpatakbo ng bagong backtest.",
    validationRejected: "Hindi na-validate ang candidate. Panatilihin ang orihinal na strategy.",
    lockedHoldoutFailed: "Hindi nakumpleto ang huling holdout check. Subukan ulit.",
  },
  "es-419": {
    runLockedHoldout: "Ejecutar validación final",
    runningLockedHoldout: "Validando con datos no utilizados...",
    lockedHoldoutHint: "La propuesta mejorada solo se puede aplicar después de superar una validación única con datos no utilizados.",
    validationApplyBlocked: "Aplicación bloqueada hasta validar",
    validationPending: "La propuesta está pendiente de la validación final.",
    validationPassed: "Propuesta validada. Puedes aplicarla y ejecutar un backtest nuevo.",
    validationRejected: "La propuesta no superó la validación. Conserva la estrategia original.",
    lockedHoldoutFailed: "No se pudo completar la validación final. Inténtalo de nuevo.",
  },
  "pt-BR": {
    runLockedHoldout: "Executar validação final",
    runningLockedHoldout: "Validando com dados ainda não usados...",
    lockedHoldoutHint: "A proposta melhorada só pode ser aplicada após passar por uma validação única com dados ainda não usados.",
    validationApplyBlocked: "Aplicação bloqueada até validar",
    validationPending: "A proposta aguarda a validação final.",
    validationPassed: "Proposta validada. Você pode aplicá-la e executar um novo backtest.",
    validationRejected: "A proposta não passou na validação. Mantenha a estratégia original.",
    lockedHoldoutFailed: "Não foi possível concluir a validação final. Tente novamente.",
  },
  ja: {
    runLockedHoldout: "最終ホールドアウト検証を実行",
    runningLockedHoldout: "未使用データで検証中...",
    lockedHoldoutHint: "改善候補は、一度限りの未使用データ検証に合格した場合のみ適用できます。",
    validationApplyBlocked: "検証完了まで適用できません",
    validationPending: "候補は最終ホールドアウト検証待ちです。",
    validationPassed: "候補の検証が完了しました。適用して新しいバックテストを実行できます。",
    validationRejected: "候補は検証に合格しませんでした。元の戦略を維持してください。",
    lockedHoldoutFailed: "最終ホールドアウト検証を完了できませんでした。もう一度お試しください。",
  },
  ko: {
    runLockedHoldout: "최종 홀드아웃 검증 실행",
    runningLockedHoldout: "사용하지 않은 데이터로 검증 중...",
    lockedHoldoutHint: "개선 후보는 일회성 미사용 데이터 검증을 통과한 뒤에만 적용할 수 있습니다.",
    validationApplyBlocked: "검증 전에는 적용할 수 없음",
    validationPending: "후보가 최종 홀드아웃 검증을 기다리고 있습니다.",
    validationPassed: "후보 검증이 완료되었습니다. 적용한 뒤 새 백테스트를 실행할 수 있습니다.",
    validationRejected: "후보가 검증을 통과하지 못했습니다. 기존 전략을 유지하세요.",
    lockedHoldoutFailed: "최종 홀드아웃 검증을 완료하지 못했습니다. 다시 시도하세요.",
  },
  ru: {
    runLockedHoldout: "Запустить финальную holdout-проверку",
    runningLockedHoldout: "Проверка на ранее не использованных данных...",
    lockedHoldoutHint: "Улучшенный кандидат можно применить только после однократной проверки на ранее не использованных данных.",
    validationApplyBlocked: "Применение заблокировано до валидации",
    validationPending: "Кандидат ожидает финальную holdout-проверку.",
    validationPassed: "Кандидат прошёл валидацию. Его можно применить и запустить новый бэктест.",
    validationRejected: "Кандидат не прошёл валидацию. Сохраните исходную стратегию.",
    lockedHoldoutFailed: "Не удалось завершить финальную holdout-проверку. Повторите попытку.",
  },
  tr: {
    runLockedHoldout: "Son holdout kontrolünü çalıştır",
    runningLockedHoldout: "Daha önce kullanılmamış veri kontrol ediliyor...",
    lockedHoldoutHint: "İyileştirilmiş aday yalnızca tek seferlik kullanılmamış veri kontrolünü geçtikten sonra uygulanabilir.",
    validationApplyBlocked: "Doğrulanana kadar uygulama kilitli",
    validationPending: "Aday son holdout kontrolünü bekliyor.",
    validationPassed: "Aday doğrulandı. Uygulayıp yeni bir backtest çalıştırabilirsiniz.",
    validationRejected: "Aday doğrulanmadı. Mevcut stratejiyi koruyun.",
    lockedHoldoutFailed: "Son holdout kontrolü tamamlanamadı. Tekrar deneyin.",
  },
  uk: {
    runLockedHoldout: "Запустити фінальну holdout-перевірку",
    runningLockedHoldout: "Перевірка на раніше не використаних даних...",
    lockedHoldoutHint: "Покращеного кандидата можна застосувати лише після одноразової перевірки на раніше не використаних даних.",
    validationApplyBlocked: "Застосування заблоковано до валідації",
    validationPending: "Кандидат очікує фінальну holdout-перевірку.",
    validationPassed: "Кандидата валідовано. Його можна застосувати й запустити новий бектест.",
    validationRejected: "Кандидат не пройшов валідацію. Збережіть початкову стратегію.",
    lockedHoldoutFailed: "Не вдалося завершити фінальну holdout-перевірку. Спробуйте ще раз.",
  },
} satisfies Record<Exclude<Locale, "en" | "vi">, ValidationStrings>;

export const validationTranslations: Partial<Record<Locale, Partial<Record<TranslationKey, string>>>> =
  completeValidationTranslations;
